import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { KeyRound, MailCheck } from "lucide-react";
import { Button, Field, Input } from "@pracownik/ui";
import { forgotPasswordSchema, type ForgotPasswordInput } from "@pracownik/shared/schemas";
import { authClient } from "@/lib/auth-client";
import { AuthLayout } from "@/layouts/auth-layout";

export function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: ForgotPasswordInput) => {
    setLoading(true);
    try {
      await authClient.requestPasswordReset({
        email: data.email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout
        title={t("resetLinkSent")}
        description={t("resetLinkSentDescription")}
        icon={<MailCheck />}
      >
        <Button asChild variant="outline" size="lg" className="w-full">
          <Link to="/login">{t("backToLogin")}</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("forgotPasswordTitle")}
      description={t("forgotPasswordDescription")}
      icon={<KeyRound />}
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Field
          htmlFor="email"
          label={t("email")}
          error={form.formState.errors.email && t("invalidEmail")}
        >
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="name@example.com"
            autoFocus
            {...form.register("email")}
          />
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={loading}>
          {t("sendResetLink")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-primary hover:underline">
          {t("backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
